/**
 * Global singleton that tracks unacknowledged booking alerts and re-plays
 * the booking ringtone every ~10 seconds while the loopUntilAck preference
 * is enabled. Components subscribe to receive count updates for UI.
 */

const STORAGE_KEY = "trigotour_notification_loop_v1";

export function readLoopPref() {
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    if (raw === null) return true;            // default ON
    return raw === "true" || raw === "1";
  } catch {
    return true;
  }
}

export function writeLoopPref(value) {
  try {
    localStorage.setItem(STORAGE_KEY, value ? "true" : "false");
  } catch { /* noop */ }
}

const LOOP_INTERVAL_MS = 10000;  // re-play ringtone every 10 seconds

const state = {
  active: new Map(),        // bookingId -> { message, registeredAt }
  loopTimer: null,
  listeners: new Set(),
};

function _emit() {
  for (const fn of state.listeners) {
    try { fn(getActiveAlerts()); } catch { /* ignore */ }
  }
}

function _playRingtone() {
  if (typeof window !== "undefined" && typeof window.__trigoPlayBookingRingtone === "function") {
    window.__trigoPlayBookingRingtone();
  }
}

function _startLoopIfNeeded() {
  if (state.loopTimer || state.active.size === 0) return;
  if (!readLoopPref()) return;                // user disabled looping
  state.loopTimer = setInterval(() => {
    if (state.active.size === 0) {
      _stopLoop();
      return;
    }
    _playRingtone();
  }, LOOP_INTERVAL_MS);
}

function _stopLoop() {
  if (state.loopTimer) {
    clearInterval(state.loopTimer);
    state.loopTimer = null;
  }
  if (typeof window !== "undefined" && typeof window.__trigoStopBookingRingtone === "function") {
    window.__trigoStopBookingRingtone();
  }
}

export function registerBookingAlert(bookingId, message) {
  if (!bookingId) return;
  if (state.active.has(bookingId)) return;    // dedup
  state.active.set(bookingId, { message: message || "", registeredAt: Date.now() });
  _emit();
  _playRingtone();                            // immediate first ring
  _startLoopIfNeeded();
}

export function acknowledgeBookingAlert(bookingId) {
  if (!bookingId) return;
  if (state.active.delete(bookingId)) {
    if (state.active.size === 0) _stopLoop();
    _emit();
  }
}

export function clearAllBookingAlerts() {
  state.active.clear();
  _stopLoop();
  _emit();
}

export function getActiveAlerts() {
  return Array.from(state.active.entries()).map(([id, v]) => ({ id, ...v }));
}

export function subscribeBookingAlerts(callback) {
  state.listeners.add(callback);
  callback(getActiveAlerts());
  return () => state.listeners.delete(callback);
}

// Expose for cross-module access (used by useWebSocket.js, DriverDashboard.js, BookingAlertBanner.js)
if (typeof window !== "undefined") {
  window.__trigoRegisterBookingAlert = registerBookingAlert;
  window.__trigoAcknowledgeBookingAlert = acknowledgeBookingAlert;
  window.__trigoClearBookingAlerts = clearAllBookingAlerts;
}
